/**
 * Date utility functions for timezone-aware formatting.
 */

/**
 * Format a UTC timestamp in the user's timezone.
 * 
 * @param utcTimestamp - ISO timestamp string (assumed to be UTC)
 * @param timezone - IANA timezone name (e.g., "America/Los_Angeles")
 * @param formatOptions - Intl.DateTimeFormat options
 * @returns Formatted date string in the user's timezone
 */
export function formatInTimezone(
  utcTimestamp: string,
  timezone: string,
  formatOptions: Intl.DateTimeFormatOptions
): string {
  try {
    const date = new Date(utcTimestamp);
    return new Intl.DateTimeFormat('en-US', {
      ...formatOptions,
      timeZone: timezone,
    }).format(date);
  } catch {
    // Fallback to local time if timezone is invalid
    const date = new Date(utcTimestamp);
    return new Intl.DateTimeFormat('en-US', formatOptions).format(date);
  }
}

/**
 * Format a date for display in newsletter history.
 * Returns "Jan 15, 2024" format.
 */
export function formatDateInTimezone(utcTimestamp: string, timezone: string): string {
  return formatInTimezone(utcTimestamp, timezone, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Format a time for display in newsletter history.
 * Returns "14:30" format (24-hour).
 */
export function formatTimeInTimezone(utcTimestamp: string, timezone: string): string {
  return formatInTimezone(utcTimestamp, timezone, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

/**
 * Format a full date and time for display.
 * Returns "January 15, 2024" format.
 */
export function formatFullDateInTimezone(utcTimestamp: string, timezone: string): string {
  return formatInTimezone(utcTimestamp, timezone, {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Parse a timestamp string as UTC and return a Date object.
 *
 * Handles ISO timestamps that may or may not have the 'Z' suffix.
 * Without this, JavaScript's new Date() interprets timestamps without
 * timezone indicators as local time, causing timezone offset issues.
 *
 * @param timestamp - ISO timestamp string (may or may not have 'Z' suffix)
 * @returns Date object representing the timestamp in UTC
 */
export function parseAsUtc(timestamp: string): Date {
  // If it already has timezone info (Z or +/-offset), parse as-is
  if (timestamp.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(timestamp)) {
    return new Date(timestamp);
  }
  // Otherwise, append 'Z' to indicate UTC
  return new Date(timestamp + 'Z');
}

/**
 * Calculate elapsed time in minutes from a UTC timestamp to now.
 *
 * @param utcTimestamp - ISO timestamp string in UTC
 * @returns Number of minutes elapsed (0 if timestamp is in the future or invalid)
 */
export function getElapsedMinutes(utcTimestamp: string): number {
  try {
    const startTime = parseAsUtc(utcTimestamp).getTime();
    const now = Date.now();
    const elapsedMs = now - startTime;
    return Math.max(0, Math.floor(elapsedMs / (1000 * 60)));
  } catch {
    return 0;
  }
}

